import { createClient } from '@supabase/supabase-js';
import { createDeskOperatorSessionHolder } from '@lemontaigu/platform';
import { deskOperatorFetch } from '@lemontaigu/platform/adapters/client';
import { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from './config.js';

/** The tab's desk session (« Qui êtes-vous ? », DESK-OPERATOR brief §2): every
 *  request below carries it as X-Desk-Operator, so audited writes name the person. */
export const deskOperatorSession = createDeskOperatorSessionHolder(
  (() => {
    try {
      return window.sessionStorage;
    } catch {
      return null;
    }
  })(),
);

export const supabase = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  global: { fetch: deskOperatorFetch(deskOperatorSession) },
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    // Magic-link flow (Q-D): the e-mailed link redirects back with tokens in
    // the URL — unlike the reception app, this client must consume them.
    detectSessionInUrl: true,
  },
});

/**
 * « Envoyer un e-mail de test » (ADR-0027, RUNBOOK §25.2): `/api/mail/test` on
 * the site project, with the operator's own session — the Function sends only
 * for an admin. Here, beside the client, because this file is the app's
 * composition root: the one place that knows a session token and a URL.
 */
export async function sendTestMail(apiBase: string, to: string): Promise<{ ok: boolean; detail: string }> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) return { ok: false, detail: 'Session expirée — reconnectez-vous.' };
  let res: Response;
  try {
    res = await fetch(`${apiBase}/api/mail/test`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${session.access_token}`, 'content-type': 'application/json' },
      body: JSON.stringify({ to }),
    });
  } catch (e) {
    return { ok: false, detail: `Service injoignable (${apiBase}) : ${e instanceof Error ? e.message : String(e)}` };
  }
  const body = (await res.json().catch(() => null)) as { ok?: boolean; providerId?: string; reason?: string; error?: string } | null;
  if (res.ok && body?.ok) return { ok: true, detail: `Accepté par Brevo (${body.providerId ?? 'sans identifiant'}).` };
  if (res.status === 404) return { ok: false, detail: 'Fonction introuvable : la version avec l’envoi d’e-mails n’est pas encore déployée.' };
  return { ok: false, detail: body?.reason ?? body?.error ?? `Échec (${res.status})` };
}
